import React, { useState } from 'react';
import { Upload, CheckCircle, AlertCircle, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

export default function CSVImporter({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setParsing(true);

    try {
      const text = await selectedFile.text();
      const lines = text.split('\n').filter(l => l.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file is empty or invalid');
        setParsing(false);
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const rows = lines.slice(1, 6); // Preview first 5 rows
      
      setPreview({
        headers,
        totalRows: lines.length - 1,
        sampleRows: rows.map(row => {
          // Simple CSV parsing (handles basic quoted fields)
          const values = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < row.length; i++) {
            const char = row[i];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              values.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          values.push(current.trim());
          
          return values.map(v => v.replace(/^"|"$/g, ''));
        })
      });
      
      toast.success(`Loaded ${lines.length - 1} rows from CSV`);
    } catch (err) {
      toast.error('Failed to parse CSV file');
      console.error(err);
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/videos/import-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`✓ Imported ${response.data.updated} videos`);
      if (response.data.created > 0) {
        toast.info(`Created ${response.data.created} new videos`);
      }
      if (response.data.errors > 0) {
        toast.warning(`${response.data.errors} rows had errors`);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'CSV import failed');
      console.error(err);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 max-w-4xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText size={24} className="text-purple-400" />
            <div>
              <h2 className="text-xl font-bold text-white">Import Video Data</h2>
              <p className="text-sm text-zinc-400">Upload CSV to update video matrix</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* File Upload */}
          {!file && (
            <label className="block">
              <div className="border-2 border-dashed border-zinc-700 rounded-lg p-12 text-center hover:border-purple-500 transition cursor-pointer">
                <Upload size={48} className="mx-auto text-zinc-600 mb-4" />
                <p className="text-white font-semibold mb-2">Click to upload CSV file</p>
                <p className="text-sm text-zinc-500">
                  Supports: title, cast, city, tags, edit_status, upload_date, 
                  jff_status, jff_price, of_vip_status, of_vip_price, etc.
                </p>
              </div>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          )}

          {/* File Selected */}
          {file && (
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-green-500" />
                  <div>
                    <div className="text-white font-medium">{file.name}</div>
                    <div className="text-sm text-zinc-500">{(file.size / 1024).toFixed(2)} KB</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setFile(null);
                    setPreview(null);
                  }}
                  className="text-sm text-red-400 hover:text-red-300"
                >
                  Remove
                </button>
              </div>

              {/* Preview */}
              {preview && (
                <div className="mt-4">
                  <div className="text-sm font-semibold text-white mb-2">
                    Preview ({preview.totalRows} rows total, showing first 5)
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          {preview.headers.map((header, idx) => (
                            <th key={idx} className="text-left p-2 text-zinc-400 font-medium">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.sampleRows.map((row, idx) => (
                          <tr key={idx} className="border-b border-zinc-800/50">
                            {row.map((cell, cellIdx) => (
                              <td key={cellIdx} className="p-2 text-zinc-300 truncate max-w-[200px]">
                                {cell || <span className="text-zinc-600">—</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Import Info */}
          <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertCircle size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-zinc-300 space-y-2">
                <p className="font-semibold text-blue-400">Import Behavior:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Videos are matched by <strong>title</strong> (case-insensitive)</li>
                  <li>Existing videos will be <strong>updated</strong> with new data</li>
                  <li>New videos (titles not found) will be <strong>created</strong></li>
                  <li><strong>upload_date</strong> will be imported and saved</li>
                  <li>Platform statuses (JFF, OF VIP, RFC) will be synced</li>
                  <li>Tags, cast, and city will be updated</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-zinc-900 border-t border-zinc-800 p-6 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!file || importing || parsing}
            className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-800 disabled:text-zinc-600 rounded-lg transition font-medium flex items-center gap-2"
          >
            {importing ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload size={18} />
                Import {preview ? `${preview.totalRows} Videos` : 'CSV'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
